/**
 * Casbin policy rows as sv_permissions serializes them through the gateway.
 *
 * The one shape worth reading twice: **`effect` is spelled differently on the way in
 * and on the way out.** The write routes take an INTEGER (`0` allow, `1` deny — the
 * proto enum); `GET /permissions/user/{id}` answers with a STRING (`"allow"` /
 * `"deny"`, because `PolicyRule.effect` is a proto `string`). A client that reads the
 * response and posts it straight back to `/permissions/toggle` sends `"allow"` where
 * `current_effect: 0` was meant — and toggle's failure mode for a wrong
 * `current_effect` is silent: the remove half matches nothing, the add half still runs,
 * and the rule ends up present under BOTH effects. {@link effectOf} is the conversion
 * that keeps that from happening; nothing here should compare `effect` by hand.
 */

/** The integer the write routes take. */
export const EFFECT_ALLOW = 0;
export const EFFECT_DENY = 1;
export type PolicyEffect = typeof EFFECT_ALLOW | typeof EFFECT_DENY;

/**
 * One direct `p` rule on a user, as the read route returns it.
 *
 * `effect` is typed as the union of both spellings rather than the string the spec
 * promises: the platform console types the same field as `0 | 1`, and being wrong
 * about which one arrives should degrade to "shown as allow", not to a crash.
 */
export type PolicyRule = {
  /** The Casbin subject — an account id here, since this route reads a user's rows. */
  subject: string;
  /** Written out in full: `system`, `company:{uuid}`, `company:{uuid}:department:{uuid}`. */
  domain: string;
  /** Free text on the wire — a resource name like `devices`, not a closed set. */
  object: string;
  /** Free text on the wire — `read`, `create`, `update`, `delete`, `manage`, … */
  action: string;
  effect: string | number;
};

/** GET /permissions/user/{user_id}. */
export type UserPolicies = {policies?: PolicyRule[]};

/**
 * GET /permissions/me — only the pairs the enforcer ALLOWS.
 *
 * A denied pair is absent, never returned with a false flag, so this list cannot be
 * used to enumerate what exists — only what is permitted.
 */
export type EffectivePermission = {resource: string; action: string};

export type EffectivePermissions = {permissions?: EffectivePermission[]};

/** Normalize either spelling of `effect` to the integer the write routes take. */
export function effectOf(effect: string | number | undefined): PolicyEffect {
  if (typeof effect === 'number') {
    return effect === EFFECT_DENY ? EFFECT_DENY : EFFECT_ALLOW;
  }
  return String(effect ?? '').toLowerCase() === 'deny' ? EFFECT_DENY : EFFECT_ALLOW;
}

/**
 * The resource names a company-scoped policy can usefully name.
 *
 * NOT a closed set on the backend — `object` is free text on every write route — so
 * this is the vocabulary the gateway actually checks inside `company:{id}`, not a
 * schema. `/control/{resource}/…` takes its resource straight from the path segment
 * (`permissions.rs`, the `parts[3]` branch), which is where most of these come from;
 * `companies`, `members` and `departments` come from the sv_organization branches.
 *
 * Platform-only objects (`licenses`, `license-devices`, `firmware`, `users`) are left
 * out on purpose: a rule naming them inside a company domain matches no request this
 * gateway produces, so offering them would be offering a rule that does nothing.
 */
export const PERM_RESOURCES = [
  'members',
  'companies',
  'departments',
  'devices',
  'desktops',
  'personnel',
  /**
   * Оклады и ведомость. Отдельный ресурс, а НЕ часть `personnel`: карточку человека
   * читает всякий с `personnel/read`, включая оператора на проходной, и оклад ему
   * видеть незачем. У ролей «руководитель отдела» и «наблюдатель» доступ сюда запрещён
   * явной deny-строкой — иначе подстановка по ресурсам в их политике отдала бы им
   * зарплаты молча и в тот же миг.
   */
  'salaries',
  'logs',
  'profiles',
  'telegram',
  'settings',
  'dashboard',
  'machines',
  'enrollments',
  'permissions',
] as const;

export type PermResource = (typeof PERM_RESOURCES)[number];

/**
 * The actions `route_to_permission` derives from a method, plus `manage`.
 *
 * `manage` is not derived from any method — it is the action the seeded `owner` policy
 * carries (`company:*, *, manage`), and Casbin's own matcher treats it as covering the
 * other four. Offering it matters: a grant of `manage` is how a second administrator
 * is made, and spelling out four rules instead is four chances to forget one.
 */
export const PERM_ACTIONS = ['read', 'create', 'update', 'delete', 'manage'] as const;

export type PermAction = (typeof PERM_ACTIONS)[number];
